import React, { useEffect, useState } from 'react';
import {
  Animated, Easing, StyleSheet, Text, View,
} from 'react-native';
import Colors from '../../../../../../constants/Colors';
import { MetricProps } from '../../../../../../graphs/AirQualityGraph';
import Style from '../../../../../../constants/Style';

interface MetricScoreCardItemProps {
  metric: MetricProps,
  showComparison: boolean,
  score: number,
  scoreCompared: number,
}

const MetricScoreCardItem = ({
  metric, score, scoreCompared, showComparison,
}: MetricScoreCardItemProps) => {
  const [barTrackWidth, setBarTrackWidth] = useState<number>(0);
  const [barWidth] = useState(new Animated.Value(0));
  const [barComparedWidth] = useState(new Animated.Value(0));

  const animation = (isCompared: boolean = false) => Animated.timing(
    isCompared ? barComparedWidth : barWidth,
    {
      duration: (isCompared ? scoreCompared : score) * 10,
      useNativeDriver: true,
      easing: Easing.ease,
      toValue: (isCompared ? scoreCompared : score) * (barTrackWidth / 100),
    },
  ).start();

  useEffect(() => {
    if (barWidth && barTrackWidth) animation();
  }, [score, barWidth, barTrackWidth]);

  useEffect(() => {
    if (barComparedWidth && barTrackWidth) animation(true);
  }, [scoreCompared, barComparedWidth, barTrackWidth]);

  return <View style={styles.wrapper}>
    <View style={styles.container}>
      <View style={Style.row}>
        <Text style={styles.title}>{metric.title}</Text>
        <View style={Style.row}>
          <Text style={styles.score}>{score ?? '-'}</Text>
          {showComparison && <Text style={styles.scoreCompared}>{scoreCompared ?? '-'}</Text>}
        </View>
      </View>
      <View
        style={styles.barTrack}
        onLayout={({ nativeEvent: { layout: { width } } }) => setBarTrackWidth(width)}
      >
        <Animated.View style={[styles.bar, { width: barWidth }]} />
      </View>
      {showComparison && <View style={[styles.barTrack, styles.barTrackCompared]}>
        <Animated.View style={[styles.bar, styles.barCompared, { width: barComparedWidth }]} />
      </View>}
    </View>
    <View style={styles.separator} />
  </View>;
};

const styles = StyleSheet.create({
  wrapper: {
    ...Style.row,
    width: '50%',
    marginTop: 8,
  },
  container: {
    flex: 1,
    padding: 8,
    backgroundColor: '#FFF',
    borderRadius: 10,
  },
  separator: {
    width: 8,
  },
  title: {
    fontSize: 12,
    lineHeight: 18,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
  },
  score: {
    fontSize: 14,
    lineHeight: 14,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksDarkBlue,
  },
  scoreCompared: {
    fontSize: 14,
    lineHeight: 14,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksYellow600,
    marginStart: 4,
  },
  barTrack: {
    backgroundColor: Colors.light.hereworksBlue50,
    height: 8,
    borderRadius: 12,
    marginTop: 8,
    overflow: 'hidden',
  },
  barTrackCompared: {
    marginTop: 4,
    backgroundColor: Colors.light.hereworksYellow100,
  },
  bar: {
    backgroundColor: Colors.light.hereworksDarkBlue,
    height: '100%',
    borderRadius: 12,
  },
  barCompared: {
    backgroundColor: Colors.light.hereworksYellow600,
  },
});

export default MetricScoreCardItem;
